import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Send, Square } from 'lucide-react';

interface MessageComposerProps {
  onSend: (content: string, options?: { voiceReply?: boolean }) => void;
  onUserTurnStart?: () => void;
  disabled?: boolean;
  /** True while the assistant is actively streaming a response — swaps Send for a Stop button. */
  isGenerating?: boolean;
  onStop?: () => void;
}

export function MessageComposer({ onSend, onUserTurnStart, disabled, isGenerating, onStop }: MessageComposerProps) {
  const [value, setValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);

  const SpeechRecognitionConstructor =
    typeof window !== 'undefined'
      ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
      : undefined;
  const voiceSupported = Boolean(SpeechRecognitionConstructor);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  }, [value]);

  const isBlocked = disabled || isGenerating;

  // Re-focus the input once it's re-enabled (e.g. after the assistant finishes replying),
  // since disabling it while a message is in flight blurs it — without this, the user has
  // to click back into the box before typing their next message.
  useEffect(() => {
    if (!isBlocked) {
      textareaRef.current?.focus();
    }
  }, [isBlocked]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isBlocked) return;
    onUserTurnStart?.();
    onSend(trimmed);
    setValue('');
    setVoiceError(null);
    textareaRef.current?.focus();
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
  };

  const startListening = () => {
    if (!SpeechRecognitionConstructor || isBlocked) return;

    onUserTurnStart?.();
    setVoiceError(null);
    setValue('');

    const recognition = new SpeechRecognitionConstructor();
    recognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = '';
    let latestTranscript = '';
    let hadRecognitionError = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript ?? '';
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      latestTranscript = `${finalTranscript}${interimTranscript}`.trim();
      setValue(latestTranscript);
    };

    recognition.onerror = (event: any) => {
      hadRecognitionError = true;
      setVoiceError(event.error === 'not-allowed' ? 'Microphone permission was denied.' : 'Voice input failed.');
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
      if (hadRecognitionError) return;

      const transcript = (finalTranscript || latestTranscript).trim();
      if (transcript) {
        onSend(transcript, { voiceReply: true });
        setValue('');
      }
    };

    recognition.start();
  };

  const handleVoiceClick = () => {
    if (isListening) {
      stopListening();
      return;
    }

    startListening();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t">
      {voiceError && (
        <div className="px-3 pt-2 text-xs text-destructive" role="alert">
          {voiceError}
        </div>
      )}
      <div className="p-3 flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isListening ? 'Listening…' : 'Type a message…'}
          disabled={isBlocked}
          rows={1}
          data-agent-target="assistant-message-input"
          data-testid="message-composer-input"
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        />
        <Button
          size="icon"
          variant={isListening ? 'secondary' : 'outline'}
          className="h-9 w-9 shrink-0"
          onClick={handleVoiceClick}
          disabled={isBlocked || !voiceSupported}
          aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
          title={voiceSupported ? (isListening ? 'Stop listening' : 'Start voice input') : 'Voice input is not supported in this browser'}
          data-agent-target="assistant-voice-button"
          data-testid="message-composer-voice"
        >
          {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        {isGenerating ? (
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 shrink-0"
            onClick={onStop}
            aria-label="Stop generating"
            data-agent-target="assistant-stop-button"
            data-testid="message-composer-stop"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        ) : (
          <Button
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={handleSend}
            disabled={disabled || !value.trim()}
            aria-label="Send message"
            data-agent-target="assistant-send-button"
            data-testid="message-composer-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
